import React from 'react'
import './filterSidebar.css'

const TimeIcon = ({ type }) => {
    if (type === 'sun') {
        return (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="1.6" />
                <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                    <line x1="12" y1="1.5" x2="12" y2="4" />
                    <line x1="12" y1="20" x2="12" y2="22.5" />
                    <line x1="1.5" y1="12" x2="4" y2="12" />
                    <line x1="20" y1="12" x2="22.5" y2="12" />
                    <line x1="4.4" y1="4.4" x2="6.2" y2="6.2" />
                    <line x1="17.8" y1="17.8" x2="19.6" y2="19.6" />
                    <line x1="4.4" y1="19.6" x2="6.2" y2="17.8" />
                    <line x1="17.8" y1="6.2" x2="19.6" y2="4.4" />
                </g>
            </svg>
        )
    }
    if (type === 'sunset') {
        return (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M6 18a6 6 0 0 1 12 0" stroke="currentColor" strokeWidth="1.6" />
                <line x1="1.5" y1="18" x2="22.5" y2="18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <path d="M9 21h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <path d="M12 3v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <path d="M4.5 8.5 7 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <path d="M19.5 8.5 17 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
        )
    }
    if (type === 'moon') {
        return (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M20 14.5A8.5 8.5 0 1 1 9.5 4 6.8 6.8 0 0 0 20 14.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
            </svg>
        )
    }
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M4 17a8 8 0 0 1 16 0" stroke="currentColor" strokeWidth="1.6" />
            <line x1="1.5" y1="17" x2="22.5" y2="17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <path d="M8 13 12 9l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}

const FilterSidebar = ({ options, filters, onChange, onReset, fromCity, toCity }) => {
    if (!options || !filters) return null

    const patch = (next) => onChange({ ...filters, ...next })

    const toggleStop = (key) => patch({ selectedStop: filters.selectedStop === key ? null : key })

    const toggleAirline = (key) => patch({ airlines: { ...filters.airlines, [key]: !filters.airlines[key] } })
    const toggleAllAirlines = () => {
        const allChecked = options.airlines.every((a) => filters.airlines[a.key])
        const next = {}
        options.airlines.forEach((a) => { next[a.key] = !allChecked })
        patch({ airlines: next })
    }

    const toggleArrivalAirport = (key) => patch({ arrivalAirports: { ...filters.arrivalAirports, [key]: !filters.arrivalAirports[key] } })
    const toggleAllArrivalAirports = () => {
        const allChecked = options.arrivalAirports.every((a) => filters.arrivalAirports[a.key])
        const next = {}
        options.arrivalAirports.forEach((a) => { next[a.key] = !allChecked })
        patch({ arrivalAirports: next })
    }

    const handlePriceMin = (e) => {
        const value = Math.min(Number(e.target.value), filters.priceRange[1] - 1)
        patch({ priceRange: [value, filters.priceRange[1]] })
    }
    const handlePriceMax = (e) => {
        const value = Math.max(Number(e.target.value), filters.priceRange[0] + 1)
        patch({ priceRange: [filters.priceRange[0], value] })
    }
    const handleDurationMin = (e) => {
        const value = Math.min(Number(e.target.value), filters.durationRange[1] - 1)
        patch({ durationRange: [value, filters.durationRange[1]] })
    }
    const handleDurationMax = (e) => {
        const value = Math.max(Number(e.target.value), filters.durationRange[0] + 1)
        patch({ durationRange: [filters.durationRange[0], value] })
    }

    const priceSpan = Math.max(options.priceMax - options.priceMin, 1)
    const durationSpan = Math.max(options.durationMax - options.durationMin, 1)

    return (
        <div className='filterSidebar'>
            <div className='filterSidebarHead'>
                <div className='filterSidebarTitle'>Filters</div>
                {onReset && (
                    <span className='filterSidebarReset commonGredientText' onClick={onReset}>Reset All</span>
                )}
            </div>

            {options.stops.length > 0 && (
                <div className='filterSection'>
                    <div className='filterSectionTitle'>Stops</div>
                    <div className='stopsRow'>
                        {options.stops.map((stop) => (
                            <div
                                key={stop.key}
                                className={`stopCard ${filters.selectedStop === stop.key ? 'active' : ''}`}
                                onClick={() => toggleStop(stop.key)}
                            >
                                <div className='stopCardLabel'>{stop.label}</div>
                                <div className='stopCardPrice'>{stop.priceLabel}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {options.priceMax > options.priceMin && (
                <div className='filterSection'>
                    <div className='filterSectionTitle'>Flight Price</div>
                    <div className='rangeSlider'>
                        <div className='rangeSlider__track' />
                        <div
                            className='rangeSlider__range'
                            style={{
                                left: `${((filters.priceRange[0] - options.priceMin) / priceSpan) * 100}%`,
                                right: `${100 - ((filters.priceRange[1] - options.priceMin) / priceSpan) * 100}%`,
                            }}
                        />
                        <input type='range' min={options.priceMin} max={options.priceMax} value={filters.priceRange[0]} onChange={handlePriceMin} />
                        <input type='range' min={options.priceMin} max={options.priceMax} value={filters.priceRange[1]} onChange={handlePriceMax} />
                    </div>
                    <div className='rangeLabels'>
                        <span>₹{filters.priceRange[0].toLocaleString('en-IN')}</span>
                        <span>₹{filters.priceRange[1].toLocaleString('en-IN')}</span>
                    </div>
                </div>
            )}

            {options.durationMax > options.durationMin && (
                <div className='filterSection'>
                    <div className='filterSectionTitle'>Duration</div>
                    <div className='rangeSlider'>
                        <div className='rangeSlider__track' />
                        <div
                            className='rangeSlider__range'
                            style={{
                                left: `${((filters.durationRange[0] - options.durationMin) / durationSpan) * 100}%`,
                                right: `${100 - ((filters.durationRange[1] - options.durationMin) / durationSpan) * 100}%`,
                            }}
                        />
                        <input type='range' min={options.durationMin} max={options.durationMax} value={filters.durationRange[0]} onChange={handleDurationMin} />
                        <input type='range' min={options.durationMin} max={options.durationMax} value={filters.durationRange[1]} onChange={handleDurationMax} />
                    </div>
                    <div className='rangeLabels'>
                        <span>{filters.durationRange[0]}h</span>
                        <span>{filters.durationRange[1]}h</span>
                    </div>
                </div>
            )}

            {options.airlines.length > 0 && (
                <div className='filterSection'>
                    <div className='filterSectionHead'>
                        <div className='filterSectionTitle'>Airlines</div>
                        <label className='filterCheckboxAll'>
                            All
                            <input
                                type='checkbox'
                                checked={options.airlines.every((a) => filters.airlines[a.key])}
                                onChange={toggleAllAirlines}
                            />
                        </label>
                    </div>
                    <div className='filterList'>
                        {options.airlines.map((airline) => (
                            <label className='filterCheckRow' key={airline.key}>
                                <div className='filterCheckLabel'>{airline.label} ({airline.count})</div>
                                <div className='filterCheckRight'>
                                    <span className='filterCheckPrice'>{airline.priceLabel}</span>
                                    <input
                                        type='checkbox'
                                        checked={!!filters.airlines[airline.key]}
                                        onChange={() => toggleAirline(airline.key)}
                                    />
                                </div>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {options.arrivalAirports.length > 1 && (
                <div className='filterSection'>
                    <div className='filterSectionHead'>
                        <div className='filterSectionTitle'>Arrival Airport</div>
                        <label className='filterCheckboxAll'>
                            All
                            <input
                                type='checkbox'
                                checked={options.arrivalAirports.every((a) => filters.arrivalAirports[a.key])}
                                onChange={toggleAllArrivalAirports}
                            />
                        </label>
                    </div>
                    <div className='filterList'>
                        {options.arrivalAirports.map((airport) => (
                            <label className='filterCheckRow' key={airport.key}>
                                <div className='filterCheckLabel'>{airport.code} - {airport.label}</div>
                                <div className='filterCheckRight'>
                                    <span className='filterCheckPrice'>{airport.priceLabel}</span>
                                    <input
                                        type='checkbox'
                                        checked={!!filters.arrivalAirports[airport.key]}
                                        onChange={() => toggleArrivalAirport(airport.key)}
                                    />
                                </div>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {options.departureSlots.some((s) => s.count > 0) && (
                <div className='filterSection'>
                    <div className='filterSectionTitle'>Departure{fromCity ? ` from ${fromCity}` : ''}</div>
                    <div className='slotGrid'>
                        {options.departureSlots.map((slot) => (
                            <div
                                key={slot.key}
                                className={`slotCard ${filters.departureSlot === slot.key ? 'active' : ''} ${!slot.count ? 'disabled' : ''}`}
                                onClick={() => slot.count && patch({ departureSlot: filters.departureSlot === slot.key ? null : slot.key })}
                            >
                                <span className='slotIcon'><TimeIcon type={slot.icon} /></span>
                                <div className='slotLabel'>{slot.label}</div>
                                <div className='slotPrice'>{slot.priceLabel || '--'}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {options.arrivalSlots.some((s) => s.count > 0) && (
                <div className='filterSection'>
                    <div className='filterSectionTitle'>Arrival{toCity ? ` at ${toCity}` : ''}</div>
                    <div className='slotGrid'>
                        {options.arrivalSlots.map((slot) => (
                            <div
                                key={slot.key}
                                className={`slotCard ${filters.arrivalSlot === slot.key ? 'active' : ''} ${!slot.count ? 'disabled' : ''}`}
                                onClick={() => slot.count && patch({ arrivalSlot: filters.arrivalSlot === slot.key ? null : slot.key })}
                            >
                                <span className='slotIcon'><TimeIcon type={slot.icon} /></span>
                                <div className='slotLabel'>{slot.label}</div>
                                <div className='slotPrice'>{slot.priceLabel || '--'}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* {onReset && (
                <div className='filterSidebarFooter' onClick={onReset}>Reset Filters</div>
            )} */}
        </div>
    )
}

export default FilterSidebar
